import { makeAutoObservable, observable, runInAction, ObservableMap } from 'mobx';
import * as utils from '@intrasync/utils';
import type RootStore from './RootStore';
import { IShape, CanvasDimensions, CanvasOffset } from '../types/shapes';

export class CadStore {
  rootStore: RootStore;
  
  shapes: ObservableMap<string, IShape> = observable.map();
  selectedShapeId: string | null = null;
  canvasDimensions: CanvasDimensions = { width: 800, height: 600 };
  scale: number = 1;
  offset: CanvasOffset = { x: 0, y: 0 };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {
      rootStore: false,
    });
  }

  get totalStackHeight(): number {
    let height = 0;
    this.shapes.forEach(shape => {
      if (shape.height) {
        height += shape.height;
      }
    });
    return height;
  }

  get selectedShape(): IShape | undefined {
    if (!this.selectedShapeId) return undefined;
    return this.shapes.get(this.selectedShapeId);
  }

  get orderedShapes(): IShape[] {
    return Array.from(this.shapes.values());
  }

  addShape = (shapeData: Omit<IShape, 'id' | 'x' | 'y'>) => {
    const newShape: IShape = {
      ...shapeData,
      id: utils.uuid(),
      x: 0, // Aligned to the left
      y: this.totalStackHeight,
    };
    
    this.shapes.set(newShape.id, newShape);
    return newShape.id;
  };

  removeShape = (shapeId: string) => {
    this.shapes.delete(shapeId);
    if (this.selectedShapeId === shapeId) {
      this.selectedShapeId = null;
    }
  };

  selectShape = (shapeId: string | null) => {
    this.selectedShapeId = shapeId;
  };

  updateShapeProperty = <K extends keyof IShape>(
    shapeId: string,
    property: K,
    value: IShape[K]
  ) => {
    const shape = this.shapes.get(shapeId);
    if (shape) {
      runInAction(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (shape as any)[property] = value;
      });
    }
  };

  clearCanvas = () => {
    this.shapes.clear();
    this.selectedShapeId = null;
  };

  setCanvasDimensions = (dimensions: CanvasDimensions) => {
    this.canvasDimensions = dimensions;
  };

  setScale = (scale: number) => {
    this.scale = scale;
  };

  setOffset = (offset: CanvasOffset) => {
    this.offset = offset;
  };

  toJSON = () => {
    return {
      shapes: Object.fromEntries(this.shapes.entries()),
      canvasDimensions: this.canvasDimensions,
      scale: this.scale,
      offset: this.offset,
    };
  };

  fromJSON = (json: Record<string, unknown>) => {
    this.clearCanvas();
    if (json.shapes) {
      const shapes = new Map<string, IShape>(Object.entries(json.shapes as Record<string, IShape>));
      this.shapes.replace(shapes);
    }
    if (json.canvasDimensions) {
      this.canvasDimensions = json.canvasDimensions as CanvasDimensions;
    }
    if (json.scale) {
      this.scale = json.scale as number;
    }
    if (json.offset) {
      this.offset = json.offset as CanvasOffset;
    }
  };
}